// src/pages/LibraryPage.tsx
import Header from '../components/Header';
import { SearchBar } from '../components/SearchBar';
import { FilterChips } from '../components/FilterChips';
import { LibraryCapsuleCard } from '../components/LibraryCapsuleCard';
import { useCapsules } from '../hooks/useCapsules';
import { useBookmarks } from '../hooks/useBookmarks';
import { useLibraryFilters } from '../hooks/useLibraryFilters';
import '../styles/LibraryPage.css';

export function LibraryPage() {
  const { capsules, loading, error } = useCapsules();
  const { isBookmarked, toggleBookmark, clearAll: clearBookmarks } = useBookmarks();
  const {
    searchQuery,
    setSearchQuery,
    activeFilters,
    toggleFilter,
    clearFilters,
    filteredCapsules,
    availableTags,
  } = useLibraryFilters(capsules);

  // Separate bookmarked capsules from the filtered list
  const bookmarkedCapsules = filteredCapsules.filter((c) => isBookmarked(c.slug));
  const otherCapsules = filteredCapsules.filter((c) => !isBookmarked(c.slug));

  if (loading) {
    return (
      <div className="library-page">
        <Header />
        <div className="library-content">
          <div className="library-loading">Loading songs...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="library-page">
        <Header />
        <div className="library-content">
          <div className="library-error">
            {error}
            <button
              className="btn btn-secondary"
              style={{ marginTop: 'var(--space-md)', display: 'block', marginInline: 'auto' }}
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const noResults = filteredCapsules.length === 0 && (searchQuery || activeFilters.length > 0);

  return (
    <div className="library-page">
      <Header />
      <div className="library-content">
        {/* Search and Filters */}
        <div className="library-controls">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search songs or artists..."
          />
          <FilterChips
            activeFilters={activeFilters}
            onToggleFilter={toggleFilter}
            onClearAll={clearFilters}
            availableTags={availableTags}
          />
        </div>

        {/* No results state */}
        {noResults && (
          <div className="library-empty">
            <svg className="library-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <h2 className="library-empty-title">No songs found</h2>
            <p className="library-empty-message">
              No songs match "{searchQuery || activeFilters.join(', ')}"
            </p>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setSearchQuery('');
                clearFilters();
              }}
            >
              Clear filters
            </button>
          </div>
        )}

        {/* Bookmarked section */}
        {bookmarkedCapsules.length > 0 && (
          <section className="library-section">
            <div className="library-section-header">
              <h2 className="library-section-title">
                <svg className="icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
                  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                </svg>
                Your Bookmarks
                <span className="library-section-count">({bookmarkedCapsules.length})</span>
              </h2>
              <button className="library-section-action" onClick={clearBookmarks}>
                Clear all
              </button>
            </div>
            <div className="library-grid">
              {bookmarkedCapsules.map((capsule) => (
                <LibraryCapsuleCard
                  key={capsule.id}
                  capsule={capsule}
                  isBookmarked={true}
                  onToggleBookmark={toggleBookmark}
                />
              ))}
            </div>
          </section>
        )}

        {/* All songs section */}
        {!noResults && (
          <section className="library-section">
            <div className="library-section-header">
              <h2 className="library-section-title">
                All Songs
                <span className="library-section-count">({otherCapsules.length})</span>
              </h2>
            </div>
            {otherCapsules.length === 0 && bookmarkedCapsules.length === 0 ? (
              <div className="library-empty">
                <svg className="library-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M9 18V5l12-2v13" />
                  <circle cx="6" cy="18" r="3" />
                  <circle cx="18" cy="16" r="3" />
                </svg>
                <h2 className="library-empty-title">No songs available yet</h2>
                <p className="library-empty-message">Check back soon!</p>
              </div>
            ) : (
              <div className="library-grid">
                {otherCapsules.map((capsule) => (
                  <LibraryCapsuleCard
                    key={capsule.id}
                    capsule={capsule}
                    isBookmarked={false}
                    onToggleBookmark={toggleBookmark}
                  />
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
